import { formatCount, tt } from '@agos/shared'

/**
 * The homepage blocks the customer site draws, and the fields each one carries.
 *
 * **FB-128.** Legacy writes twelve `if (moduleKey === "…")` branches, ~570 lines of hand-rolled forms,
 * and ends with `return null`. Anything else — a module added by a migration, by another client, or by
 * `POST /homepage-modules` with a base key the catalogue does not list, which the endpoint accepts —
 * opens to an **empty editor**: no fields, no message, and no way to tell that from a module with
 * nothing to configure. Two of the twelve genuinely have nothing to configure, so the blank is
 * indistinguishable from working.
 *
 * A schema says the same thing in a table. An unknown key falls to `null` here too, but the page knows
 * the difference between *this module has no settings* and *nobody has described this module*.
 */

export type FieldKind = 'text' | 'multiline' | 'url' | 'image' | 'number' | 'boolean' | 'colour' | 'products'

export interface ModuleField {
  key: string
  label: string
  kind: FieldKind
  hint?: string
  /**
   * The example text legacy shows in the empty box - §9.329.
   *
   * **Ported, not invented.** Legacy passes one to most of its hand-rolled inputs and this schema
   * dropped every one of them, which is a large part of *"Settings are not completed… they looking
   * shaby"*: a box labelled *Ponuka - návestie* over nothing tells you less than the same box
   * showing `Špeciálna ponuka · Jedine u nás`. They are the owner's own strings from their own site.
   */
  placeholder?: string
  rows?: number
  min?: number
  max?: number
}

export interface ModuleSchema {
  /** The base key, without the `:2` suffix a duplicated block carries. */
  baseKey: string
  label: string
  description: string
  fields: ModuleField[]
  /** True when the block genuinely has nothing to configure — not the same as an unknown block. */
  selfContained?: boolean
}

/** Three identically-shaped blocks, so the schema says it once. */
function guaranteeBlocks(): ModuleField[] {
  return [1, 2, 3].flatMap((index) => [
    { key: `block${index}Title`, label: `${tt('Blok')} ${index} - ${tt('nadpis')}`, kind: 'text' as const },
    {
      key: `block${index}Text`,
      label: `${tt('Blok')} ${index} - ${tt('text')}`,
      kind: 'multiline' as const,
      rows: 2,
    },
  ])
}

export const MODULE_SCHEMAS: readonly ModuleSchema[] = [
  {
    baseKey: 'promo_banner',
    label: tt('Promo panel'),
    description: tt('Farebný panel s ponukou, vernostným programom a hodnotením.'),
    fields: [
      { key: 'gradientFrom', label: tt('Prechod od'), kind: 'colour' },
      { key: 'gradientVia', label: tt('Prechod cez'), kind: 'colour' },
      { key: 'gradientTo', label: tt('Prechod do'), kind: 'colour' },
      { key: 'offerEnabled', label: tt('Zobraziť ponuku'), kind: 'boolean' },
      { key: 'offerKicker', label: tt('Ponuka - návestie'), kind: 'text', placeholder: tt('Špeciálna ponuka · Jedine u nás') },
      { key: 'offerTitle', label: tt('Ponuka - nadpis'), kind: 'text', placeholder: tt('malá šunková za') },
      { key: 'offerHighlight', label: tt('Ponuka - zvýraznenie'), kind: 'text' },
      { key: 'offerSubtitle', label: tt('Ponuka - podnadpis'), kind: 'multiline', placeholder: tt('Kúp 2, tretia za 2 € · Platí iba pri online objednávke'), rows: 2 },
      { key: 'offerSideBadge', label: tt('Ponuka - bočný štítok'), kind: 'text', placeholder: tt('Iba u nás') },
      { key: 'offerBadgeText', label: tt('Štítok - text'), kind: 'text' },
      { key: 'offerBadgeLabel', label: tt('Štítok - popis'), kind: 'text' },
      { key: 'offerBadgeBadgeColor', label: tt('Štítok - farba'), kind: 'colour' },
      { key: 'offerBadgeLabelColor', label: tt('Štítok - farba popisu'), kind: 'colour' },
      { key: 'loyaltyEnabled', label: tt('Zobraziť vernostný program'), kind: 'boolean' },
      { key: 'loyaltyTitle', label: tt('Vernosť - nadpis'), kind: 'text', placeholder: tt('Vernostný program') },
      { key: 'loyaltySubtitle', label: tt('Vernosť - podnadpis'), kind: 'multiline', placeholder: tt('Za každú objednávku body do ďalšej'), rows: 2 },
      // **These two were missing entirely - §9.329.** `module-content.ts:377` renders them in the
      // customer app and the owner's own `promo_banner` row stores both, so they were two chips a
      // customer could see and the manager could not reach. Not a new feature: a gap in the schema.
      { key: 'loyaltyTag1', label: tt('Vernosť - štítok 1'), kind: 'text', placeholder: tt('Zľavy na meniny') },
      { key: 'loyaltyTag2', label: tt('Vernosť - štítok 2'), kind: 'text', placeholder: tt('Zľavové kupóny') },
      { key: 'pointsCardEnabled', label: tt('Zobraziť kartu bodov'), kind: 'boolean' },
      { key: 'pointsCardText', label: tt('Karta bodov - text'), kind: 'text', placeholder: tt('Využi vernostné body a kupóny za vernost a ziskaj zľavu') },
      { key: 'emailHintEnabled', label: tt('Zobraziť tip na e-mail'), kind: 'boolean' },
      { key: 'emailHintText', label: tt('Tip na e-mail - text'), kind: 'text', placeholder: tt('Stačí sa registrovať alebo pri objednávke zadať e-mail…') },
      { key: 'finalNote', label: tt('Záverečná poznámka'), kind: 'text', placeholder: tt('… a mnoho ďalšieho') },
      { key: 'socialEnabled', label: tt('Zobraziť sociálne siete'), kind: 'boolean' },
      { key: 'googleRating', label: tt('Hodnotenie Google'), kind: 'text' },
    ],
  },
  {
    baseKey: 'banner',
    label: tt('Hlavný banner'),
    description: tt('Obrázok cez celú šírku s otváracími hodinami a výzvou.'),
    fields: [
      // The sizes are legacy's own, from the labels on its upload boxes. Nobody can guess them and
      // a banner cropped to the wrong aspect is the most visible thing on the site.
      { key: 'desktopImageUrl', label: tt('Obrázok - počítač'), kind: 'image', hint: tt('Verzia pre počítač, 1440×520 px') },
      { key: 'mobileImageUrl', label: tt('Obrázok - mobil'), kind: 'image', hint: tt('Verzia pre mobil, 375×493 px') },
      { key: 'hoursText', label: tt('Otváracie hodiny'), kind: 'text', placeholder: tt('Po - So 10:00 - 22:00, Ne 13:00 - 22:00') },
      { key: 'phoneText', label: tt('Telefón'), kind: 'text', placeholder: tt('0910 406 434') },
      { key: 'addressText', label: tt('Adresa'), kind: 'text', placeholder: tt('M. R. Štefánika 3, 942 01 Šurany') },
      { key: 'captionText', label: tt('Popisok'), kind: 'multiline', placeholder: tt('Vitajte u nás'), rows: 2 },
      { key: 'ctaText', label: tt('Tlačidlo - text'), kind: 'text', placeholder: tt('Objednať teraz') },
      { key: 'ctaUrl', label: tt('Tlačidlo - odkaz'), kind: 'url' },
      { key: 'ctaPosition', label: tt('Tlačidlo - umiestnenie'), kind: 'text' },
      { key: 'overlayDim', label: tt('Stmavenie obrázka'), kind: 'number', min: 0, max: 100 },
    ],
  },
  {
    baseKey: 'social_links',
    label: tt('Sociálne siete'),
    description: tt('Odkazy na Facebook, Instagram a TikTok, s hodnotením Google.'),
    fields: [
      { key: 'title', label: tt('Nadpis'), kind: 'text', placeholder: tt('Sledujte nás') },
      { key: 'subtitle', label: tt('Podnadpis'), kind: 'multiline', placeholder: tt('Zostaňte v obraze - nové akcie, recepty a chuťovky.'), rows: 2 },
      { key: 'googleRating', label: tt('Hodnotenie Google'), kind: 'text' },
    ],
  },
  {
    baseKey: 'personalized_greeting',
    label: tt('Osobný pozdrav'),
    description: tt('Karty pre prihláseného zákazníka - obľúbené, naposledy objednané, niečo nové.'),
    fields: [
      { key: 'showFavoriteCard', label: tt('Zobraziť kartu obľúbených'), kind: 'boolean' },
      { key: 'favoriteLabel', label: tt('Obľúbené - popis'), kind: 'text' },
      { key: 'favoriteTemplates', label: tt('Obľúbené - vety'), kind: 'multiline', hint: tt('Jedna veta na riadok - zákazníkovi sa zobrazí jedna z nich. {produkt} sa nahradí názvom.'), rows: 3 },
      { key: 'showLastOrderedCard', label: tt('Zobraziť kartu posledného'), kind: 'boolean' },
      { key: 'lastOrderedLabel', label: tt('Posledné - popis'), kind: 'text' },
      { key: 'lastOrderedTemplates', label: tt('Posledné - vety'), kind: 'multiline', hint: tt('Jedna veta na riadok - zákazníkovi sa zobrazí jedna z nich. {produkt} sa nahradí názvom.'), rows: 3 },
      { key: 'showVarietyCard', label: tt('Zobraziť kartu novinky'), kind: 'boolean' },
      { key: 'varietyLabel', label: tt('Novinka - popis'), kind: 'text', placeholder: tt('Vyskúšaj niečo nové') },
      { key: 'varietyTemplates', label: tt('Novinka - vety'), kind: 'multiline', hint: tt('Jedna veta na riadok - zákazníkovi sa zobrazí jedna z nich. {produkt} sa nahradí názvom.'), rows: 3 },
      { key: 'ctaAddText', label: tt('Tlačidlo - pridať'), kind: 'text', placeholder: tt('Pridať do košíka') },
      { key: 'ctaAddedText', label: tt('Tlačidlo - pridané'), kind: 'text', placeholder: tt('Pridané!') },
      { key: 'ctaViewText', label: tt('Tlačidlo - zobraziť'), kind: 'text', placeholder: tt('Pozrieť') },
    ],
  },
  {
    baseKey: 'most_ordered',
    label: tt('Najčastejšie objednávané'),
    description: tt(
      'Šesť produktov, ktoré si daný zákazník objednáva najčastejšie. Zobrazí sa len prihlásenému zákazníkovi s históriou objednávok.',
    ),
    fields: [],
    selfContained: true,
  },
  {
    baseKey: 'popular_dishes',
    label: tt('Obľúbené jedlá'),
    description: tt('Ručne vybrané produkty.'),
    fields: [
      { key: 'title', label: tt('Nadpis'), kind: 'text', placeholder: tt('Obľúbené jedlá') },
      { key: 'productIds', label: tt('Produkty'), kind: 'products' },
    ],
  },
  {
    baseKey: 'chef_recommendations',
    label: tt('Odporúčania šéfkuchára'),
    description: tt('Ručne vybrané produkty.'),
    fields: [
      { key: 'title', label: tt('Nadpis'), kind: 'text', placeholder: tt('Novinky') },
      { key: 'productIds', label: tt('Produkty'), kind: 'products' },
    ],
  },
  {
    baseKey: 'guarantees',
    label: tt('Zaručujeme'),
    description: tt('Tri bloky s prísľubmi. Prázdny blok sa nezobrazí.'),
    fields: guaranteeBlocks(),
  },
  {
    baseKey: 'virtual_tour',
    label: tt('Virtuálna prehliadka'),
    description: tt('Vložený rámec s prehliadkou priestoru.'),
    fields: [
      { key: 'title', label: tt('Nadpis'), kind: 'text', placeholder: tt('Pozrite sa k nám') },
      { key: 'subtitle', label: tt('Podnadpis'), kind: 'multiline', rows: 2 },
      { key: 'iframeUrl', label: tt('Adresa prehliadky'), kind: 'url', hint: tt('Adresa vloženej prehliadky, napríklad z Google Maps alebo Matterport'), placeholder: tt('https://...') },
      { key: 'heightDesktop', label: tt('Výška - počítač'), kind: 'number', min: 200, max: 1000 },
      { key: 'heightMobile', label: tt('Výška - mobil'), kind: 'number', min: 200, max: 800 },
    ],
  },
  {
    baseKey: 'about_us',
    label: tt('O nás'),
    description: tt('Krátky text s obrázkom.'),
    fields: [
      { key: 'imageUrl', label: tt('Obrázok'), kind: 'image', hint: tt('Fotografia prevádzky alebo jedla') },
      { key: 'description', label: tt('Text'), kind: 'multiline', placeholder: tt('Pravá talianska pizza podľa originálnej receptúry'), rows: 4 },
      { key: 'linkUrl', label: tt('Odkaz'), kind: 'url', placeholder: tt('https://maps.app.goo.gl/...') },
    ],
  },
  {
    baseKey: 'newsletter',
    label: tt('Newsletter'),
    description: tt('Prihlásenie na odber.'),
    fields: [
      { key: 'title', label: tt('Nadpis'), kind: 'text', placeholder: tt('Odoberajte novinky') },
      { key: 'subtitle', label: tt('Podnadpis'), kind: 'multiline', placeholder: tt('Buďte prvý, kto sa dozvie o akciách'), rows: 2 },
      { key: 'ctaText', label: tt('Tlačidlo'), kind: 'text', placeholder: tt('Prihlásiť sa') },
      { key: 'placeholder', label: tt('Nápoveda v poli'), kind: 'text', placeholder: tt('Váš email...') },
    ],
  },
  {
    baseKey: 'testimonials',
    label: tt('Referencie'),
    description: tt('Recenzie zákazníkov.'),
    fields: [
      { key: 'title', label: tt('Nadpis'), kind: 'text', placeholder: tt('Čo o nás hovoria') },
      { key: 'subtitle', label: tt('Podnadpis'), kind: 'multiline', placeholder: tt('Recenzie našich spokojných zákazníkov'), rows: 2 },
    ],
  },
]

export const CATALOG_BASE_KEYS: readonly string[] = MODULE_SCHEMAS.map((schema) => schema.baseKey)

/** `promo_banner:2` is a second copy of `promo_banner`. */
export function baseKeyOf(moduleKey: string): string {
  return moduleKey.split(':')[0] ?? moduleKey
}

export function copyNumber(moduleKey: string): number {
  const suffix = moduleKey.split(':')[1]
  const parsed = Number.parseInt(suffix ?? '', 10)
  return Number.isFinite(parsed) ? parsed : 1
}

export function schemaFor(moduleKey: string): ModuleSchema | null {
  const base = baseKeyOf(moduleKey)
  return MODULE_SCHEMAS.find((schema) => schema.baseKey === base) ?? null
}

export function moduleLabel(moduleKey: string): string {
  const schema = schemaFor(moduleKey)
  const copy = copyNumber(moduleKey)
  const name = schema?.label ?? moduleKey
  return copy > 1 ? `${name} (${copy})` : name
}

export type EditorState = 'fields' | 'self-contained' | 'unknown'

/**
 * What the editor can offer for a block.
 *
 * The distinction legacy cannot make: *Najčastejšie objednávané* has nothing to configure and shows a
 * blank; a block nobody has described also shows a blank. One is finished, the other is a gap.
 */
export function editorState(moduleKey: string): EditorState {
  const schema = schemaFor(moduleKey)
  if (schema === null) return 'unknown'
  return schema.fields.length === 0 ? 'self-contained' : 'fields'
}

// ─── the blocks themselves ───────────────────────────────────────────────────

export interface HomepageModule {
  id?: number
  moduleKey?: string
  enabled?: boolean
  sortOrder?: number
  content?: Record<string, unknown> | null
  updatedAt?: string
}

export interface Block {
  id: number
  moduleKey: string
  enabled: boolean
  sortOrder: number
  content: Record<string, unknown>
}

export function blocksOf(modules: HomepageModule[]): Block[] {
  return modules
    .map((module, index) => ({
      id: module.id ?? index,
      moduleKey: module.moduleKey ?? '',
      enabled: module.enabled === true,
      sortOrder: module.sortOrder ?? index,
      content: module.content ?? {},
    }))
    .sort((left, right) => left.sortOrder - right.sortOrder)
}

/** Moving a block renumbers every one of them, because `sortOrder` is the whole order. */
export function move(blocks: Block[], from: number, to: number): Block[] {
  if (from === to || from < 0 || to < 0 || from >= blocks.length || to >= blocks.length) {
    return blocks
  }
  const next = [...blocks]
  const [moved] = next.splice(from, 1)
  if (moved === undefined) return blocks
  next.splice(to, 0, moved)
  return next.map((block, index) => ({ ...block, sortOrder: index }))
}

export function toggle(blocks: Block[], moduleKey: string): Block[] {
  return blocks.map((block) =>
    block.moduleKey === moduleKey ? { ...block, enabled: !block.enabled } : block,
  )
}

export function setContent(
  blocks: Block[],
  moduleKey: string,
  content: Record<string, unknown>,
): Block[] {
  return blocks.map((block) => (block.moduleKey === moduleKey ? { ...block, content } : block))
}

/** Exactly what `PUT /homepage-modules-bulk` reads. */
export function bulkPayload(blocks: Block[]): {
  moduleKey: string
  enabled: boolean
  sortOrder: number
  content: Record<string, unknown>
}[] {
  return blocks.map((block) => ({
    moduleKey: block.moduleKey,
    enabled: block.enabled,
    sortOrder: block.sortOrder,
    content: block.content,
  }))
}

/**
 * Whether anything has been changed but not saved.
 *
 * Legacy toggles, reorders and edits content in local state and writes only when *Uložiť moduly* is
 * pressed. That is a reasonable design and it is kept — but nothing on the screen says a switch that
 * looks off is still on for every customer, so somebody who toggles and navigates away has changed
 * nothing while believing otherwise.
 */
export function isDirty(blocks: Block[], original: Block[]): boolean {
  return JSON.stringify(bulkPayload(blocks)) !== JSON.stringify(bulkPayload(original))
}

export interface BlockCounts {
  total: number
  enabled: number
  unknown: number
}

export function blockCounts(blocks: Block[]): BlockCounts {
  return {
    total: blocks.length,
    enabled: blocks.filter((block) => block.enabled).length,
    unknown: blocks.filter((block) => editorState(block.moduleKey) === 'unknown').length,
  }
}

/** A base key can be added again; the endpoint gives the copy its `:N` suffix. */
export function addableKeys(): readonly string[] {
  return CATALOG_BASE_KEYS
}

// ─── FB-127: the setting that does nothing ───────────────────────────────────

/**
 * **FB-127.** Two controls for the homepage's contents, and one of them is not wired to anything.
 *
 * `settings/design.activeModules` is a list of Slovak **labels** — on this data
 * `["Odporúčania šéfkuchára", "Obľúbené jedlá", "Zaručujeme", "O nás"]` — edited on this page as a set
 * of checkboxes, persisted to the server, and **read by nothing**. Grepped across every artifact in the
 * legacy tree: three files mention it, and all three are writers or the storage route. No customer app,
 * no other manager page, no API consumer.
 *
 * The homepage is actually composed from `homepage_modules` — twelve rows with `enabled` and
 * `sortOrder`, edited a few centimetres below on the same page. On this data **ten of the twelve are
 * enabled** while the checkbox list names four, so the two controls disagree and only one of them
 * matters. Switching a block off in the checkbox list changes nothing a customer sees.
 */
export const DEAD_MODULE_LABELS: readonly string[] = [
  tt('Pútavý Banner'),
  tt('Odporúčania šéfkuchára'),
  tt('Obľúbené jedlá'),
  tt('Zaručujeme'),
  tt('O nás'),
]

/** How far apart the two lists are, so the page can say it rather than assert it. */
export function activeModulesDisagree(activeModules: string[], blocks: Block[]): boolean {
  const enabled = blocks.filter((block) => block.enabled).length
  return activeModules.length !== enabled
}

// ─── §9.329: the two controls that had no control ────────────────────────────

/**
 * **The owner:** *"these bloks are still not working properly. Settings are not completed, like
 * uploading image, they looking shaby... basically, I hate it"*.
 *
 * Two of the eight field kinds this schema declares had no working editor, and the schema is what
 * made that easy to miss - `FieldInput` fell through to a plain text box for `image` and printed a
 * count with an apology for `products`. So:
 *
 *  - **`image`** rendered a bare input **expecting the operator to paste a URL**. Legacy has a
 *    drag-and-drop box with a preview, a size limit and a type check (`ModuleImageUpload`), and
 *    AGOS already had the endpoint - `usePostUploadImage`, used by the burger-box slot editor. So
 *    this is a **missed port over machinery that already existed**, not a new feature.
 *  - **`products`** printed *"výber produktov sa tu zatiaľ nedá meniť"*. Legacy has a real picker
 *    grouped by category with a search box. Two blocks - *Obľúbené jedlá* and *Odporúčania
 *    šéfkuchára* - have `productIds` as their only meaningful setting, so those two blocks could
 *    not be configured at all.
 *
 * The logic lives here rather than in the components so it can be tested without a DOM.
 */

/** Legacy's own ceiling, and the same message: `PNG, JPG, WebP · max. 5 MB`. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024

export type ImageProblem = 'not-an-image' | 'too-large'

/**
 * Why a chosen file cannot be uploaded, or null when it can.
 *
 * Checked before the request rather than after: legacy checks the same two things client-side, and a
 * 12 MB photo over a restaurant's uplink fails slowly and says nothing useful when it does.
 */
export function imageProblem(file: { type: string; size: number }): ImageProblem | null {
  if (!file.type.startsWith('image/')) return 'not-an-image'
  if (file.size > MAX_IMAGE_BYTES) return 'too-large'
  return null
}

export function imageProblemMessage(problem: ImageProblem): string {
  return problem === 'not-an-image'
    ? tt('Vyberte obrázok - PNG, JPG, WebP alebo GIF.')
    : tt('Obrázok je príliš veľký. Najviac 5 MB.')
}

export interface PickerProduct {
  id: number
  name: string
  categoryName?: string | null
  active?: boolean
}

export interface ProductGroup {
  category: string
  products: PickerProduct[]
}

/**
 * The picker's rows: the products that match the search, grouped by category.
 *
 * **Inactive products are kept and flagged rather than hidden.** A block that already names one is
 * the case that matters - dropping it from the list would make an existing selection look like it
 * had been lost, and the count would disagree with the chips. Legacy shows everything
 * `GET /products` answers, which includes them.
 */
export function productGroups(products: PickerProduct[], search: string): ProductGroup[] {
  const needle = search.trim().toLowerCase()
  const groups = new Map<string, PickerProduct[]>()
  for (const product of products) {
    if (needle !== '' && !product.name.toLowerCase().includes(needle)) continue
    const category = product.categoryName ?? tt('Ostatné')
    const bucket = groups.get(category)
    if (bucket === undefined) groups.set(category, [product])
    else bucket.push(product)
  }
  return [...groups.entries()]
    .map(([category, list]) => ({ category, products: list }))
    .sort((left, right) => left.category.localeCompare(right.category, 'sk'))
}

/** A chosen product goes in, a chosen one comes out. The order is the order they were picked in. */
export function toggleProductId(ids: number[], id: number): number[] {
  return ids.includes(id) ? ids.filter((current) => current !== id) : [...ids, id]
}

/**
 * One chosen product moved by `delta` places - §9.797.
 *
 * The customer homepage draws the block's products in this order (`resolvePicked`), so the order is
 * the setting. A move past either end leaves the list as it was rather than wrapping round, and an id
 * that is not chosen moves nothing.
 */
export function moveProductId(ids: number[], id: number, delta: number): number[] {
  const from = ids.indexOf(id)
  const to = from + delta
  if (from < 0 || delta === 0 || to < 0 || to >= ids.length) return ids
  const next = ids.filter((current) => current !== id)
  next.splice(to, 0, id)
  return next
}

/** One chosen product put first - the owner's *novinky*, which a pick appends to the end - §9.797. */
export function moveProductIdToFront(ids: number[], id: number): number[] {
  return ids.indexOf(id) <= 0 ? ids : [id, ...ids.filter((current) => current !== id)]
}

export interface ChosenProduct {
  id: number
  /** Null when the product is no longer in the catalogue - it stays listed so the count agrees. */
  name: string | null
  active?: boolean
}

/** The chosen products in their stored order, which is the order the homepage draws - §9.797. */
export function chosenInOrder(ids: number[], known: PickerProduct[]): ChosenProduct[] {
  return ids.map((id) => {
    const product = known.find((candidate) => candidate.id === id)
    return product === undefined
      ? { id, name: null }
      : { id, name: product.name, ...(product.active === undefined ? {} : { active: product.active }) }
  })
}

/**
 * The ids stored on a block, narrowed.
 *
 * `content` is `jsonb`, so this can be anything - and it is `[]` on both blocks that use it today.
 * A non-numeric entry is dropped rather than coerced: `Number('x')` is `NaN`, and a `NaN` in this
 * list would silently select nothing while counting as a selection.
 */
export function productIdsOf(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is number => typeof entry === 'number' && Number.isInteger(entry))
}

/** What the picker says above the list, so the count and the chips cannot disagree. */
export function selectionNote(ids: number[], known: PickerProduct[]): string {
  const missing = ids.filter((id) => !known.some((product) => product.id === id)).length
  if (ids.length === 0) return tt('Nič nie je vybrané - blok sa nezobrazí.')
  if (missing === 0) return `${tt('Vybraných')}: ${formatCount(ids.length)}`
  // A stored id whose product has been deleted. Saying so beats a count that does not match what is
  // on screen, which is how somebody concludes the picker is broken.
  return `${tt('Vybraných')}: ${formatCount(ids.length)} (${formatCount(missing)} ${tt('už v ponuke nie je')})`
}
